import type { ValueOrCondition } from '@unifyapps/defs/types/conditionalValueFilters';
import type { LCColorValue } from '@unifyapps/defs/types/styleObject';
import _isObject from 'lodash/isObject';
import _reduce from 'lodash/reduce';
import _values from 'lodash/values';
import type { ConditionalValueFilters } from '../../../utils/conditionalValues';
import { getConditionalPropertyValue } from '../../../utils/conditionalValues';
import { isDynamicValue } from '../../utils/dynamicBindings';
import { getCustomStyleValue } from './styles/getCustomStyles';
import type { CustomStyleValue } from './styles/types';
import type { getStyleItemConfig } from './styles/utils';
import type { AppearanceStyles, StyleAccumulator, StyleResult, StyleValue } from './types';

// maps Tailwind background-size class names to CSS property values
// used to convert compound conditional values to inline styles
const BG_SIZE_CSS_MAP: Record<string, string> = {
  'bg-auto': 'auto',
  'bg-cover': 'cover',
  'bg-contain': 'contain',
  'bg-[length:100%_100%]': '100% 100%',
};

// maps Tailwind background-repeat class names to CSS property values
const BG_REPEAT_CSS_MAP: Record<string, string> = {
  'bg-repeat': 'repeat',
  'bg-no-repeat': 'no-repeat',
  'bg-repeat-x': 'repeat-x',
  'bg-repeat-y': 'repeat-y',
};

function getUpdatedAppearanceStyleFromConditionValues({
  appearanceStyles,
  conditionalValueFilters,
  key,
  styleItemsConfig,
}: {
  appearanceStyles: StyleAccumulator;
  conditionalValueFilters:
    | ConditionalValueFilters<string>
    | Record<string, ConditionalValueFilters<string>>
    | ConditionalValueFilters<CustomStyleValue>
    | Record<string, ConditionalValueFilters<CustomStyleValue>>;
  key: string;
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>;
}) {
  const conditionalFilter =
    'conditionalValueFilters' in conditionalValueFilters
      ? (conditionalValueFilters as ConditionalValueFilters<string>)
      : _values(conditionalValueFilters)[0];

  const value = getConditionalPropertyValue<string | CustomStyleValue>({
    value: conditionalFilter,
  });

  // If the resolved conditional value is a customStyle, handle it here.
  // Currently this is only possible/supported for custom colors.
  if (typeof value === 'object' && Object.keys(value)[0] === 'custom') {
    const styleValue = _values(value);
    return processCustomStyle(appearanceStyles, styleValue, key, styleItemsConfig);
  }

  // handle compound background image object from conditional values
  // when backgroundImage conditions store { backgroundImage, backgroundSize, backgroundRepeat }
  // as a single compound object, extract each part and apply as inline styles
  if (
    typeof value === 'object' &&
    value !== null &&
    'backgroundImage' in value &&
    styleItemsConfig?.[key]?.picker === 'imagePicker'
  ) {
    const {
      backgroundImage = '',
      backgroundSize,
      backgroundRepeat,
    } = value as {
      backgroundImage?: string;
      backgroundSize?: string;
      backgroundRepeat?: string;
    };

    const nextAcc = processCustomStyle(appearanceStyles, backgroundImage, key, styleItemsConfig);

    return {
      ...nextAcc,
      style: {
        ...nextAcc.style,
        ...(backgroundSize && { backgroundSize: BG_SIZE_CSS_MAP[backgroundSize] }),
        ...(backgroundRepeat && { backgroundRepeat: BG_REPEAT_CSS_MAP[backgroundRepeat] }),
      },
    };
  }

  return {
    ...appearanceStyles,
    className: value ? `${appearanceStyles.className} ${value}` : appearanceStyles.className,
  };
}

const processConditionalValue = (
  acc: StyleAccumulator,
  val:
    | Record<string, ConditionalValueFilters<string>>
    | ConditionalValueFilters<string>
    | ConditionalValueFilters<CustomStyleValue>
    | Record<string, ConditionalValueFilters<CustomStyleValue>>,
  key: string,
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>,
): StyleAccumulator => {
  return getUpdatedAppearanceStyleFromConditionValues({
    appearanceStyles: acc,
    conditionalValueFilters: val,
    key,
    styleItemsConfig,
  });
};

const processCustomStyle = (
  acc: StyleAccumulator,
  styleValue: string | string[] | CustomStyleValue[],
  key: string,
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>,
): StyleAccumulator => {
  return getCustomStyleValue({
    styleValue,
    key,
    initialStylesAndClassName: acc,
    styleItemsConfig,
  });
};

const processRegularStyle = (
  acc: StyleAccumulator,
  styleString: string | number,
  key?: string,
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>,
): StyleAccumulator => {
  if (styleString === 'none') {
    return acc;
  }

  // Merge style object with the final style object.
  const styleObject = key
    ? styleItemsConfig?.[key]?.options?.all?.find((option) => option.id === styleString)
        ?.styleObject
    : undefined;

  if (styleObject) {
    return {
      className: acc.className,
      style: { ...acc.style, ...styleObject },
    };
  }

  return {
    className: `${acc.className} ${styleString as string}`,
    style: acc.style,
  };
};

const getBoxStyleObject = ({
  property,
  direction,
  value,
}: {
  property: 'padding' | 'margin';
  direction: string;
  value: string | number;
}): Record<string, string | number> | null => {
  switch (direction) {
    case 'all':
      return { [property]: value };
    case 'x':
      return { [`${property}Inline`]: value };
    case 'y':
      return { [`${property}Block`]: value };
    case 't':
      return { [`${property}Top`]: value };
    case 'r':
      return { [`${property}InlineEnd`]: value };
    case 'b':
      return { [`${property}Bottom`]: value };
    case 'l':
      return { [`${property}InlineStart`]: value };
    default:
      return null;
  }
};

const getGapStyleObject = ({
  direction,
  value,
}: {
  direction: string;
  value: string | number;
}): Record<string, string | number> | null => {
  switch (direction) {
    case 'all':
      return { gap: value };
    case 'x':
      return { columnGap: value };
    case 'y':
      return { rowGap: value };
    default:
      return null;
  }
};

// Tailwind border-style token → CSS border-style value
const BORDER_STYLE_TOKEN_TO_CSS: Record<string, string> = {
  'border-solid': 'solid',
  'border-dashed': 'dashed',
  'border-dotted': 'dotted',
  'border-none': 'none',
};

const BORDER_SIDE_BY_DIRECTION: Record<string, 'Top' | 'Right' | 'Bottom' | 'Left'> = {
  t: 'Top',
  r: 'Right',
  b: 'Bottom',
  l: 'Left',
};

const getBorderRadiusStyleObject = ({
  direction,
  value,
}: {
  direction: string;
  value: string | number;
}): Record<string, string | number> | null => {
  switch (direction) {
    case 'all':
      return { borderRadius: value };
    case 'tl':
      return { borderStartStartRadius: value };
    case 'tr':
      return { borderStartEndRadius: value };
    case 'bl':
      return { borderEndStartRadius: value };
    case 'br':
      return { borderEndEndRadius: value };
    default:
      return null;
  }
};

const getBorderWidthStyleObject = ({
  direction,
  value,
}: {
  direction: string;
  value: string | number;
}): Record<string, string | number> | null => {
  switch (direction) {
    case 'all':
      return { borderWidth: value };
    case 'x':
      return { borderInlineWidth: value };
    case 'y':
      return { borderBlockWidth: value };
    case 't':
      return { borderTopWidth: value };
    case 'r':
      return { borderInlineEndWidth: value };
    case 'b':
      return { borderBottomWidth: value };
    case 'l':
      return { borderInlineStartWidth: value };
    default:
      return null;
  }
};

const getOverflowStyleObject = ({
  direction,
  value,
}: {
  direction: string;
  value: string | number;
}): Record<string, string | number> | null => {
  switch (direction) {
    case 'all':
      return { overflow: value };
    case 'x':
      return { overflowX: value };
    case 'y':
      return { overflowY: value };
    default:
      return null;
  }
};

const getDirectionalStyleObject = ({
  property,
  direction,
  value,
}: {
  property: string;
  direction: string;
  value: string | number;
}): Record<string, string | number> | null => {
  if (property === 'padding' || property === 'margin') {
    return getBoxStyleObject({ property, direction, value });
  }

  if (property === 'gap' || property === 'gapSecondary') {
    return getGapStyleObject({ direction, value });
  }

  if (property === 'borderRadius') {
    switch (direction) {
      case 'all':
        return { borderRadius: value };
      case 'tl':
        return { borderTopLeftRadius: value };
      case 'tr':
        return { borderTopRightRadius: value };
      case 'bl':
        return { borderBottomLeftRadius: value };
      case 'br':
        return { borderBottomRightRadius: value };
      default:
        return null;
    }
  }

  if (property === 'borderWidth') {
    const side = BORDER_SIDE_BY_DIRECTION[direction];
    if (direction === 'all') return { borderWidth: value };
    if (side) return { [`border${side}Width`]: value };
    return null;
  }

  if (property === 'borderStyle') {
    const cssValue =
      typeof value === 'string' ? (BORDER_STYLE_TOKEN_TO_CSS[value] ?? value) : value;
    const side = BORDER_SIDE_BY_DIRECTION[direction];
    if (direction === 'all') return { borderStyle: cssValue };
    if (side) return { [`border${side}Style`]: cssValue };
    return null;
    // return getBorderRadiusStyleObject({ direction, value });
  }

  // if (property === 'borderWidth') {
  //   return getBorderWidthStyleObject({ direction, value });
  // }

  if (property === 'overflow') {
    return getOverflowStyleObject({ direction, value });
  }

  return null;
};

// Resolves a directional border color value (conditional / `{ custom }`) to an inline `border*Color` style.
// New data always arrives as `{ custom: 'var(--…)' | '#hex' }` and is applied directly in case of borderColor.
const applyDirectionalBorderColorValue = (
  acc: StyleAccumulator,
  direction: string,
  directionValue: LCColorValue | ValueOrCondition<LCColorValue>,
): StyleAccumulator => {
  let resolved: LCColorValue | undefined;

  if (
    _isObject(directionValue) &&
    'conditionalValueFilters' in (directionValue as Record<string, unknown>)
  ) {
    resolved = getConditionalPropertyValue<LCColorValue>({
      value: directionValue as ValueOrCondition<LCColorValue>,
    });
  } else if (_isObject(directionValue) && 'custom' in (directionValue as Record<string, unknown>)) {
    resolved = directionValue as LCColorValue;
  }

  const side = BORDER_SIDE_BY_DIRECTION[direction];
  let cssProp: string | null = null;
  if (direction === 'all') {
    cssProp = 'borderColor';
  } else if (side) {
    cssProp = `border${side}Color`;
  }
  if (!cssProp) return acc;

  if (typeof resolved === 'object' && Object.keys(resolved)[0] === 'custom') {
    return {
      className: acc.className,
      style: { ...acc.style, [cssProp]: resolved.custom as string },
    };
  }

  // legacy: conditional resolved to a palette string token → apply as class
  if (typeof resolved === 'string') {
    return {
      className: `${acc.className} ${resolved}`,
      style: acc.style,
    };
  }

  return acc;
};

const SIZE_STYLE_KEYS = new Set([
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
]);

const normalizeSizeValue = (value: string | number, key: string) => {
  if (typeof value !== 'string' || !SIZE_STYLE_KEYS.has(key)) {
    return value;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === 'full' || normalized === 'fill') {
    return '100%';
  }
  if (normalized === 'auto') {
    return 'auto';
  }

  return value;
};

// Resolve option list for a specific direction, falling back to "all".
const getOptionsForDirection = (
  styleItemsConfig: ReturnType<typeof getStyleItemConfig> | undefined,
  key: string,
  direction: string,
) => {
  const options = styleItemsConfig?.[key]?.options;
  if (!options) return undefined;
  return options[direction as keyof typeof options] ?? options.all;
};

// Decide whether a directional value is a class name or a custom inline style.
const applyDirectionalStyleValue = (
  acc: StyleAccumulator,
  key: string,
  direction: string,
  value: string | number,
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>,
): StyleAccumulator => {
  const options = getOptionsForDirection(styleItemsConfig, key, direction);
  if (!options || options.length === 0) {
    return processRegularStyle(acc, value);
  }

  const isOptionValue = options?.some((option) => option.id === value);
  const isDynamic = typeof value === 'string' && isDynamicValue(value);

  if ((!isOptionValue && !isDynamic) || key === 'borderStyle') {
    const normalizedValue = normalizeSizeValue(value, key);
    const directionalStyle = getDirectionalStyleObject({
      property: key,
      direction,
      value: normalizedValue,
    });

    if (directionalStyle) {
      return {
        className: acc.className,
        style: { ...acc.style, ...directionalStyle },
      };
    }

    if (direction === 'all') {
      const styleObject = styleItemsConfig?.[key]?.getStyleObject?.(String(normalizedValue)) ?? {
        [key]: normalizedValue,
      };

      return {
        className: acc.className,
        style: { ...acc.style, ...styleObject },
      };
    }
  }

  return processRegularStyle(acc, value);
};

// Keep className output stable when multiple directions resolve to the same token.
const dedupeClassName = (className: string) => {
  const tokens = className.trim().split(/\s+/).filter(Boolean);
  return Array.from(new Set(tokens)).join(' ');
};

// determines if a value contains conditional filters
const hasConditionalFilters = (val: StyleValue): boolean => {
  if (!_isObject(val)) return false;

  const hasDirectConditionalFilters = 'conditionalValueFilters' in val;
  const firstValue = _values(val)[0];
  const hasNestedConditionalFilters =
    _isObject(firstValue) && 'conditionalValueFilters' in firstValue;

  return Boolean(hasDirectConditionalFilters || hasNestedConditionalFilters);
};

export const getBlockAppearanceAndStyles = ({
  appearanceStyles,
  styleItemsConfig,
}: {
  appearanceStyles: AppearanceStyles;
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>;
}): StyleResult => {
  // early return for empty styles
  if (!appearanceStyles) {
    return { className: '' };
  }

  const initialAcc: StyleAccumulator = { className: '', style: {} };

  // process each style entry and build up the result
  const result = _reduce(
    appearanceStyles,
    (acc, val: StyleValue, key) => {
      if (!val) return acc;

      // handle conditional values
      if (hasConditionalFilters(val)) {
        return processConditionalValue(
          acc,
          val as
            | Record<string, ConditionalValueFilters<string>>
            | ConditionalValueFilters<string>
            | ConditionalValueFilters<CustomStyleValue>
            | Record<string, ConditionalValueFilters<CustomStyleValue>>,
          key,
          styleItemsConfig,
        );
      }

      // Object values can be directional (all/x/y/t/r/b/l) and/or custom.
      if (_isObject(val) && !Array.isArray(val)) {
        const valueObject = val as Record<string, unknown>;
        let nextAcc = acc;

        if ('custom' in valueObject) {
          const customValue = valueObject.custom;
          if (_isObject(customValue) && !Array.isArray(customValue)) {
            Object.entries(customValue as Record<string, unknown>).forEach(
              ([direction, directionValue]) => {
                // object direction values: `{ custom: '…' }` or conditional objects
                if (typeof directionValue === 'object' && directionValue !== null) {
                  nextAcc = applyDirectionalBorderColorValue(
                    nextAcc,
                    direction,
                    directionValue as LCColorValue | ValueOrCondition<LCColorValue>,
                  );
                  return;
                }

                if (typeof directionValue === 'string' || typeof directionValue === 'number') {
                  nextAcc = applyDirectionalStyleValue(
                    nextAcc,
                    key,
                    direction,
                    directionValue,
                    styleItemsConfig,
                  );
                }
              },
            );
          } else if (typeof customValue === 'string' || typeof customValue === 'number') {
            const normalizedValue = normalizeSizeValue(customValue, key);
            nextAcc = processCustomStyle(nextAcc, String(normalizedValue), key, styleItemsConfig);
          }
        }

        Object.entries(valueObject).forEach(([direction, directionValue]) => {
          if (direction === 'custom') return;
          // object direction values: `{ custom: '…' }` or conditional objects
          if (typeof directionValue === 'object' && directionValue !== null) {
            nextAcc = applyDirectionalBorderColorValue(
              nextAcc,
              direction,
              directionValue as LCColorValue | ValueOrCondition<LCColorValue>,
            );
            return;
          }
          if (typeof directionValue === 'string' || typeof directionValue === 'number') {
            nextAcc = applyDirectionalStyleValue(
              nextAcc,
              key,
              direction,
              directionValue,
              styleItemsConfig,
            );
          }
        });

        return nextAcc;
      }
      if (typeof val === 'string' || typeof val === 'number') {
        return processRegularStyle(acc, val, key, styleItemsConfig);
      }

      return acc;
    },
    initialAcc,
  );

  return {
    className: dedupeClassName(result.className),
    style: result.style,
  };
};
